import { useEffect, useRef, useState } from "react";

import { CodeCell, CodeValue, CopyCodeButton } from "./AdminStudies.styles";

interface ISignupCodeCellProps {
  code: string | null;
}

/**
 * The study's signup code with a one-click copy (EN-5155). Retool rendered it
 * as a plain string, but handing the code to a site is what the page is for,
 * and re-typing it is where a transcription error would enter enrolment.
 */
export function SignupCodeCell({ code }: ISignupCodeCellProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  if (!code) return <span>-</span>;

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      // Clipboard access needs a secure context; the portal is HTTPS
      // everywhere, and a denied permission just leaves the code readable.
      await navigator.clipboard?.writeText(code);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <CodeCell>
      <CodeValue>{code}</CodeValue>
      <CopyCodeButton
        type="button"
        copied={copied}
        aria-label={`Copy signup code ${code}`}
        onClick={handleCopy}
      >
        {copied ? "Copied" : "Copy"}
      </CopyCodeButton>
    </CodeCell>
  );
}
